import AddNotesIcon from '@assets/images/icons/add-notes.svg';
import LockedForumIcon from '@assets/images/icons/locked-forum.svg';
import LogoBlue from '@assets/images/logo-blue.svg';
import { MemberSidebar } from '@features/members/components/MemberOrg';
import Button, { ButtonVariants } from '@features/ui-form/components/Button';
import { AdminBadge } from '@features/ui-atoms/components/AdminBadge';
import Tooltip from '@features/ui-atoms/components/Tooltip';
import VersionInfo from '@features/ui-atoms/components/VersionInfo';
import { canPublishSst, canUseAdminArea } from '@entities/User/permissions';
import { useViewer } from '@features/user/hooks/useViewer';
import { useCurrentUser, useLogout } from '@features/user/hooks/useAuth';
import {
  ListAlt,
  ForumOutlined,
  FolderCopyOutlined,
  GroupOutlined,
  LogoutOutlined,
  HelpOutlineOutlined,
  AdminPanelSettings,
} from '@mui/icons-material';
import { styled } from '@mui/joy';
import { useConversations } from '@root/src/entities/Conversation/hooks';
import getRoute, { isRouteActive } from '@services/routeList';
import { formatFullName } from '@utils/formatters/strings';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useState, useEffect, type ReactElement } from 'react';
import { UrlObject } from 'url';

interface NavigationItem {
  label: string;
  route: UrlObject;
  id: string;
  icon: ReactElement;
  disabled?: boolean;
}

const Sidebar = () => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { data: currentUser, isLoading } = useCurrentUser();
  const { member, firstName, lastName } = currentUser ?? {};
  const logout = useLogout();
  const fullName = formatFullName(firstName, lastName);
  const { data: conversationsData } = useConversations();
  const unreadCount = conversationsData?.items?.reduce((acc, conv) => acc + (conv.unread || 0), 0) || 0;
  const viewer = useViewer();

  // Add client-side state to prevent hydration mismatch
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const navigationItems: NavigationItem[] = [
    {
      label: t('navigation.sstForum'),
      route: getRoute.sstForum(),
      id: 'sst-forum',
      icon: <ListAlt />,
    },
    {
      label: t('navigation.dedicatedForum'),
      route: getRoute.dedicatedForum(),
      id: 'dedicated-forum',
      icon: <LockedForumIcon />,
    },
    {
      label: t('navigation.messages'),
      route: getRoute.messages(),
      id: 'messages',
      icon: <ForumOutlined />,
    },
    {
      label: t('navigation.inventory.title'),
      route: getRoute.inventory(),
      id: 'inventory',
      icon: <FolderCopyOutlined />,
    },
    {
      label: t('navigation.directory.title'),
      route: getRoute.directory(),
      id: 'members',
      icon: <GroupOutlined />,
    },
  ];

  const bottomNavigationItems: NavigationItem[] = [
    ...(canUseAdminArea(viewer)
      ? [
          {
            label: t('navigation.admin'),
            route: getRoute.adminEvents(),
            id: 'admin-events',
            icon: <AdminPanelSettings />,
          },
        ]
      : []),
    {
      label: t('navigation.helpAndContact'),
      route: getRoute.help(),
      id: 'help',
      icon: <HelpOutlineOutlined />,
    },
    /*
    {
      label: t('navigation.glossary'),
      route: getRoute.glossary(),
      id: 'glossary',
      icon: IconName.book,
    },
    */
  ];

  return (
    <Container>
      <TopSection>
        <ProfileSection>
          <LogoBlue style={{ width: '130px', height: '32px' }} />
          {isClient && !isLoading && (
            <>
              <ProfileName>{fullName}</ProfileName>
              <OrgRow>
                {member?.id && <MemberSidebar id={member.id} />}
                {viewer?.superuser ? (
                  <Tooltip title={t('superadmin.banner')}>
                    <BadgeRow>
                      <AdminBadge user={currentUser} testId="admin-badge-sidebar" />
                    </BadgeRow>
                  </Tooltip>
                ) : (
                  <BadgeRow>
                    <AdminBadge user={currentUser} testId="admin-badge-sidebar" />
                  </BadgeRow>
                )}
              </OrgRow>
              <ProfileLink onClick={() => void router.push(getRoute.profileSpaceObjects())}>
                {t('navigation.profile.title')}
              </ProfileLink>
            </>
          )}
        </ProfileSection>

        <NavigationList>
          {navigationItems.map(item => {
            const button = (
              <Button
                disabled={item?.disabled}
                variant={ButtonVariants.MENU}
                onClick={() => void router.push(item.route)}
                startDecorator={
                  item.id === 'messages' ? (
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      {item.icon}
                      {unreadCount > 0 && (
                        <span data-testid="messages-unread-dot" style={{ position: 'absolute', top: -4, right: -4 }}>
                          <CircleActive />
                        </span>
                      )}
                    </div>
                  ) : (
                    item.icon
                  )
                }
                label={item.label}
                sx={{
                  '&.active': {
                    backgroundColor: theme => theme.palette.blueLight,
                    color: theme => theme.palette.bluePrimary,
                    fontWeight: 500,
                  },
                  color: theme => theme.palette.grey5,
                }}
                className={isRouteActive(router.pathname, item.route.pathname, item.id) ? 'active' : ''}
              />
            );

            return <NavigationItem key={item.id}>{button}</NavigationItem>;
          })}
        </NavigationList>
      </TopSection>

      <BottomSection>
        {canPublishSst(viewer) && (
          <BottomSectionContainer>
            <Button
              sx={{
                ' .MuiButton-startDecorator': {
                  marginRight: '6px', // because our svg have no container
                },
              }}
              onClick={() => void router.push(getRoute.sstForumPublish())}
              startDecorator={<AddNotesIcon />}
              label={t('navigation.publish')}
            />
          </BottomSectionContainer>
        )}

        <BottomContainer>
          <NavigationList>
            {bottomNavigationItems.map(item => (
              <NavigationItem key={item.id}>
                <Button
                  variant={ButtonVariants.MENU}
                  onClick={() => void router.push(item.route)}
                  startDecorator={item.icon}
                  label={item.label}
                  className={router.pathname === item.route.pathname ? 'active' : ''}
                  sx={{
                    '&.active': {
                      backgroundColor: theme => theme.palette.blueLight,
                      color: theme => theme.palette.bluePrimary,
                    },
                    color: 'inherit',
                  }}
                />
              </NavigationItem>
            ))}
            <NavigationItem>
              <Button
                variant={ButtonVariants.MENU}
                onClick={() => void logout.mutateAsync()}
                isLoading={logout.isPending}
                startDecorator={<LogoutOutlined />}
                label={t('navigation.logout')}
                sx={{
                  color: 'inherit',
                  '&.active': {
                    fontWeight: 500,
                  },
                }}
              />
            </NavigationItem>
          </NavigationList>
          <VersionInfo />
        </BottomContainer>
      </BottomSection>
    </Container>
  );
};

export default Sidebar;

const Container = styled('div')({
  position: 'fixed',
  left: 0,
  top: 0,
  width: 'var(--width-sidebar)',
  height: '100vh',
  backgroundColor: 'white',
  borderRight: '1px solid #E4E3E8',
  zIndex: 'var(--z-index-sidebar)',
  display: 'flex',
  flexDirection: 'column',
});

const TopSection = styled('div')({
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
});

const BottomSection = styled('div')({
  flex: '0 0 auto',
  display: 'flex',
  flexDirection: 'column',
});

const BottomSectionContainer = styled('div')(({ theme }) => ({
  marginLeft: '16px',
  marginRight: '16px',
  paddingBottom: '32px',
  borderBottom: `1px solid ${theme.palette.grey2}`,
}));

const ProfileSection = styled('div')({
  padding: '20px 20px 0px 20px',
  marginBottom: '46px',
  width: '100%',
  maxWidth: 'calc(var(--width-sidebar)',
});

const ProfileName = styled('h2')(({ theme }) => ({
  ...theme.typography['body-md'],
  margin: '16px 0 4px 0',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  color: theme.palette.black,
}));

const BadgeRow = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
});

const OrgRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
});

const ProfileLink = styled('a')(({ theme }) => ({
  cursor: 'pointer',
  display: 'block',
  marginTop: '4px',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  ...theme.typography['body-regular'],
  color: theme.palette.grey5,
  position: 'relative',
  textDecoration: 'none',
  paddingBottom: '2px',
  width: 'fit-content',

  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '1px',
    backgroundColor: theme.palette.grey5,
  },

  '&:hover': {
    color: theme.palette.bluePrimary,

    '&::before': {
      backgroundColor: theme.palette.bluePrimary,
    },
  },
}));

const NavigationList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '0 8px',
});

const NavigationItem = styled('div')(({ theme }) => ({
  width: '100%',
  color: theme.palette.grey5,
}));

const BottomContainer = styled('div')({
  marginTop: '16px',
  marginBottom: '46px',
  paddingTop: '16px',
});

const CircleActive = styled('div')(({ theme }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: theme.palette.bluePrimary,
}));
